<template>
  <div class="tc-preview-panel" data-testid="timesheet-preview-panel">
    <!-- Always mounted so screen readers announce the text change. -->
    <span class="visually-hidden" role="status" data-testid="draft-loading-status">{{ draftLoading ? 'Loading draft' : '' }}</span>
    <div class="tc-preview-scroll tc-scroll">
      <table class="tc-preview-table" role="grid" aria-label="Timesheet preview grid" :aria-busy="draftLoading">
        <thead>
          <tr>
            <th class="tc-preview-col-project" scope="col" :data-testid="`preview-col-wbs1`">{{ wbs1Label }}</th>
            <th v-if="showWbs2" class="tc-preview-col-phase" scope="col" :data-testid="`preview-col-wbs2`">{{ wbs2Label }}</th>
            <th v-if="showWbs3" class="tc-preview-col-task" scope="col" :data-testid="`preview-col-wbs3`">{{ wbs3Label }}</th>
            <th
              v-for="(day, di) in periodDays"
              :key="di"
              scope="col"
              class="tc-preview-col-day"
              data-testid="preview-col-day"
              :class="{ 'tc-preview-col-day--weekend': day.isNonWorking }"
            >
              <div class="tc-preview-col-day-dow">{{ day.dow[0] }}</div>
              <div class="tc-preview-col-day-num">{{ day.date }}</div>
            </th>
            <th scope="col" class="tc-preview-col-total">Σ</th>
            <th scope="col" class="tc-preview-col-actions"><span class="visually-hidden">Row actions</span></th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-if="rows.length === 0 && !draftLoading"
          >
            <td
              :colspan="periodDays.length + wbsColCount"
              class="tc-preview-empty"
              data-testid="preview-empty-state"
            >No entries yet.</td>
          </tr>

          <template v-for="(row, ri) in rows" :key="row.rowKey">
          <!-- Group header: "In Vantagepoint · Nh" / "Draft · Nh" (replaces per-row chips) -->
          <tr
            v-if="groupHeaders.get(ri)"
            class="tc-preview-group-row"
            :data-testid="`preview-group-${groupHeaders.get(ri)!.id}`"
          >
            <td :colspan="periodDays.length + wbsColCount" class="tc-preview-group-cell">
              {{ groupHeaders.get(ri)!.label }}
            </td>
          </tr>
          <tr
            :data-row-wbs1="row.wbs1"
            :class="[
              row.isGhost ? 'tc-preview-row-ghost' : (row.isDraft ? 'tc-preview-row-draft' : 'tc-preview-row-existing'),
              row.isDraft ? 'tc-preview-row-draft--clickable' : '',
              isHighlighted(row) ? 'tc-preview-row--highlighted' : '',
            ]"
            :data-testid="row.isDraft ? 'preview-draft-row' : 'preview-existing-row'"
            @click="row.isDraft && emit('rowClick', row.rowKey)"
          >
            <!-- Project column: sticky left. Draft rows: click opens the row-level
                 project picker (whole-row move — edit standard rule 1). -->
            <td
              class="tc-preview-wbs-cell"
              data-testid="preview-project-cell"
              :class="row.isDraft ? 'tc-preview-wbs-cell--draft' : 'tc-preview-wbs-cell--existing'"
              :aria-label="row.isDraft ? `${row.wbs1} — change project` : undefined"
              @click="onProjectCellClick(row, $event)"
            >
              <div class="tc-preview-wbs-inner">
                <!-- Confidence rail: how much to trust the row's target -->
                <ConfidenceBar
                  v-if="row.isDraft && row.confBar"
                  :state="row.confBar"
                  variant="bar"
                  class="tc-preview-wbs-conf"
                  :data-testid="`preview-conf-bar-${row.confBar}`"
                />
                <div class="tc-preview-wbs-body">
                  <div class="tc-preview-wbs-code txt-mono">
                    {{ row.wbs1 }}
                    <span
                      v-if="regeneratingRowKeys.has(row.rowKey)"
                      class="spinner-border spinner-border-sm ms-1"
                      role="status"
                      aria-label="Generating note…"
                      data-testid="row-note-regenerating"
                    ></span>
                  </div>
                  <div class="tc-preview-wbs-name">{{ row.wbs1Name }}</div>
                  <div v-if="row.client" class="tc-preview-wbs-client">{{ row.client }}</div>
                </div>
              </div>
            </td>

            <!-- Phase column (hidden when tenant showWbs2=N) -->
            <td
              v-if="showWbs2"
              class="tc-preview-phase-cell"
              :class="[
                !row.isDraft ? 'tc-preview-phase-cell--existing' : '',
                row.isPhased ? 'tc-preview-phase-cell--phased' : '',
                (row.isDraft && row.isPhased) ? 'tc-preview-cell--editable' : '',
                (row.isDraft && row.needsPhase) ? 'tc-preview-phase-cell--needs-phase' : '',
              ]"
              :data-testid="row.isDraft ? 'preview-phase-cell' : undefined"
              :aria-label="phaseCellAriaLabel(row)"
              @click="onPhaseCellClickCapture(row, $event, 'phase')"
            >
              <template v-if="row.needsPhase">
                <button
                  type="button"
                  class="tc-btn-needs"
                  data-testid="preview-phase-select-btn"
                  @click.stop="emit('phaseCellClick', row.rowKey, $event.currentTarget as HTMLElement, 'phase')"
                >Select phase ▾</button>
              </template>
              <template v-else-if="row.isDraft && row.wbs2">
                <button
                  type="button"
                  class="tc-preview-phase-resolved-btn"
                  data-testid="preview-phase-resolved-btn"
                  :aria-label="`Phase ${row.wbs2} — tap to change`"
                  @click.stop="emit('phaseCellClick', row.rowKey, $event.currentTarget as HTMLElement, 'phase')"
                >
                  <span class="tc-preview-phase-code txt-mono">{{ row.wbs2 }}</span>
                  <span v-if="row.wbs2Name" class="tc-preview-phase-name text-truncate">{{ row.wbs2Name }}</span>
                </button>
              </template>
              <template v-else-if="row.wbs2">
                <span class="tc-preview-phase-code txt-mono">{{ row.wbs2 }}</span>
                <span v-if="row.wbs2Name" class="tc-preview-phase-name text-truncate">{{ row.wbs2Name }}</span>
              </template>
              <template v-else>
                <span class="tc-preview-phase-blank">—</span>
              </template>
            </td>

            <!-- Task column (hidden when tenant showWbs3=N) -->
            <td
              v-if="showWbs3"
              class="tc-preview-task-cell"
              :class="[
                !row.isDraft ? 'tc-preview-task-cell--existing' : '',
                taskCellActionable(row) ? 'tc-preview-cell--editable' : '',
                (row.isDraft && row.needsTask) ? 'tc-preview-task-cell--needs-task' : '',
              ]"
              :data-testid="row.isDraft ? 'preview-task-cell' : undefined"
              :aria-label="taskCellAriaLabel(row)"
              @click="onPhaseCellClickCapture(row, $event, 'task')"
            >
              <template v-if="row.isDraft && row.needsTask">
                <button
                  type="button"
                  class="tc-btn-needs"
                  data-testid="preview-task-select-btn"
                  :aria-label="`${row.wbs1} — select task`"
                  @click.stop="emit('phaseCellClick', row.rowKey, $event.currentTarget as HTMLElement, 'task')"
                >Select task ▾</button>
              </template>
              <template v-else-if="row.wbs3">
                <span class="tc-preview-task-code txt-mono">{{ row.wbs3 }}</span>
                <span v-if="row.wbs3Name" class="tc-preview-task-name text-truncate">{{ row.wbs3Name }}</span>
              </template>
              <template v-else>
                <span class="tc-preview-task-blank">—</span>
              </template>
            </td>

            <!-- Day cells -->
            <td
              v-for="(day, di) in periodDays"
              :key="di"
              class="tc-preview-day-cell"
              :class="[
                day.isNonWorking ? 'tc-preview-day-cell--weekend' : '',
                (row.days[di] || row.ghostDay === di) ? 'tc-preview-day-cell--value' : 'tc-preview-day-cell--empty',
                row.uncertain[di] ? 'tc-preview-day-cell--uncertain' : '',
                row.isDraft ? 'tc-preview-day-cell--editable' : '',
                row.ghostDay === di ? 'tc-preview-day-cell--ghost' : '',
                isCellHighlighted(row, di) ? 'tc-preview-day-cell--selected' : '',
              ]"
              :aria-label="cellAriaLabel(row, di, day.dow)"
              @click="onDayCellClick(row, di, $event)"
            >{{ ghostCellText(row, di) ?? (row.days[di] ? row.days[di]!.toFixed(2) : '') }}</td>
            <td class="tc-preview-total-cell">{{ rowTotal(row).toFixed(2) }}</td>
            <!-- Row end, the placement Vantagepoint uses. Shown on hover and on
                 focus: hover alone would put it out of reach of a keyboard. -->
            <td class="tc-preview-actions-cell">
              <button
                v-if="row.isDraft && !row.isGhost"
                type="button"
                class="tc-preview-row-remove"
                :aria-label="`Remove ${row.wbs1} from the draft`"
                :title="`Remove ${row.wbs1} from the draft`"
                :data-testid="`preview-row-remove-${row.rowKey}`"
                @click.stop="emit('rowRemove', row.rowKey)"
              >
                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 5h10M6.5 5V3.5h3V5M5 5l.6 8h4.8L11 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </td>
          </tr>
          </template>

          <!-- The draft is still on its way: VP rows above are real, these hold its place. -->
          <template v-if="draftLoading">
            <tr class="tc-preview-group-row" aria-hidden="true">
              <td :colspan="periodDays.length + wbsColCount" class="tc-preview-group-cell">Your Draft</td>
            </tr>
            <tr
              v-for="n in 3"
              :key="`draft-placeholder-${n}`"
              class="placeholder-glow"
              aria-hidden="true"
              data-testid="draft-placeholder-row"
            >
              <td :colspan="periodDays.length + wbsColCount" class="py-2">
                <span class="placeholder col-12 rounded"></span>
              </td>
            </tr>
          </template>
        </tbody>

        <tfoot>
          <tr class="tc-preview-footer-row">
            <td class="tc-preview-footer-label">Daily total</td>
            <td v-if="showWbs2" class="tc-preview-phase-cell tc-preview-phase-cell--footer"></td>
            <td v-if="showWbs3" class="tc-preview-task-cell tc-preview-task-cell--footer"></td>
            <td
              v-for="(day, di) in periodDays"
              :key="di"
              class="tc-preview-footer-day"
              :class="[
                day.isNonWorking ? 'tc-preview-footer-day--weekend' : '',
                dailyTotals[di] > 10 ? 'tc-preview-footer-day--over' :
                dailyTotals[di] >= 7 ? 'tc-preview-footer-day--enough' :
                dailyTotals[di] > 0 ? 'tc-preview-footer-day--partial' :
                'tc-preview-footer-day--empty',
              ]"
            >{{ dailyTotals[di] ? dailyTotals[di].toFixed(2) : '' }}</td>
            <td class="tc-preview-footer-total" data-testid="preview-footer-total">{{ grandTotal.toFixed(2) }}h</td>
            <td class="tc-preview-actions-cell"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { TimesheetRow } from '../api/client';
import type { BillingTarget, DraftResponse } from '../api/draft';
import { formatMonthDay, periodDayCount } from '../utils/period';
import ConfidenceBar from './ui/ConfidenceBar.vue';
import {
  buildPreviewRows,
  cellAriaLabel,
  ghostCellText,
  isCellHighlighted as isCellHighlightedFor,
  isRowHighlighted,
  regeneratingRowKeys as regeneratingRowKeysFor,
  rowTotal,
  taskCellActionable,
  type PreviewRow,
} from './timesheetGridRows';

interface PeriodDay {
  // MM/DD
  date: string;
  dow: string;
  isNonWorking: boolean;
}

const props = withDefaults(defineProps<{
  vpRows: TimesheetRow[];
  periodStart: string;
  periodEnd: string;
  draftRows?: DraftResponse;
  // The draft request is in flight: placeholder rows stand in for the draft block.
  draftLoading?: boolean;
  highlightWbs?: string;
  // Which block the highlight belongs to: a card in Published / Duplicate points
  // at the VP row carrying its hours, not at a draft row for the same project.
  highlightScope?: 'draft' | 'vp';
  // The clicked event's exact day cell — filled while the row highlight is on.
  highlightCell?: { wbs1: string; dayIndexes: number[]; scope?: 'draft' | 'vp' } | null;
  // eventIds with an in-flight note auto-regenerate (Item 5, ux-batch-2026-07)
  // — surfaces as a small spinner next to the owning row's WBS1 code.
  regeneratingEventIds?: Set<string>;
  // Tenant WBS labels (from useTimesheetSettings). Defaults match VP standard names.
  wbs1Label?: string;
  wbs2Label?: string;
  wbs3Label?: string;
  // When false, the Phase / Task columns are hidden from the grid.
  // Phase/task data still exists server-side and drives the picker; this is
  // display-only suppression. Phased pickers remain functional when showWbs2 is
  // false because phase data lives on the row — only the column is hidden.
  showWbs2?: boolean;
  showWbs3?: boolean;
  // Live preview of where a staged resolver pick would land: marks the cell on
  // the matching draft row, or appends a ghost row when none exists yet.
  ghostCell?: { target: BillingTarget; dayIndex: number; hours: number } | null;
  // Weekdays VP does not work, as Date.getDay() numbers — the shading follows
  // the tenant's week, so a Sun–Thu tenant greys Fri/Sat.
  nonWorkingDays?: number[];
}>(), {
  wbs1Label: 'Project',
  wbs2Label: 'Phase',
  wbs3Label: 'Task',
  showWbs2: true,
  showWbs3: true,
  nonWorkingDays: () => [0, 6],
});

// Columns either side of the day cells: project + optional phase + optional task,
// then the row total and the row-action column. Drives the full-width colspans.
const wbsColCount = computed(() => 1 + (props.showWbs2 ? 1 : 0) + (props.showWbs3 ? 1 : 0) + 2);

const regeneratingRowKeys = computed(() => regeneratingRowKeysFor(props.draftRows, props.regeneratingEventIds));

const emit = defineEmits<{
  // rowKey identifies the specific row (wbs1|wbs2|wbs3 path).
  rowClick: [rowKey: string];
  projectCellClick: [rowKey: string, anchor: HTMLElement];
  cellBadgeClick: [rowKey: string, dayIndex: number, anchor: HTMLElement];
  // field distinguishes a Phase-cell click (phase + task cascade) from a
  // Task-cell click (task only) — both scope to the row's project/phase.
  phaseCellClick: [rowKey: string, anchor: HTMLElement, field: 'phase' | 'task'];
  rowRemove: [rowKey: string];
}>();

// Cell click handlers stop propagation only when the cell is actionable, so
// clicks on inert cells still bubble to the row's highlight toggle.
function onProjectCellClick(row: PreviewRow, e: MouseEvent): void {
  if (!row.isDraft) return;
  e.stopPropagation();
  emit('projectCellClick', row.rowKey, e.currentTarget as HTMLElement);
}

function onPhaseCellClickCapture(row: PreviewRow, e: MouseEvent, field: 'phase' | 'task'): void {
  const actionable = field === 'task' ? taskCellActionable(row) : row.isDraft && row.isPhased;
  if (!actionable) return;
  e.stopPropagation();
  emit('phaseCellClick', row.rowKey, e.currentTarget as HTMLElement, field);
}

function onDayCellClick(row: PreviewRow, di: number, e: MouseEvent): void {
  if (!row.isDraft) return;
  e.stopPropagation();
  emit('cellBadgeClick', row.rowKey, di, e.currentTarget as HTMLElement);
}

const periodDays = computed((): PeriodDay[] => {
  const result: PeriodDay[] = [];
  const dayCount = periodDayCount(props.periodStart, props.periodEnd);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(props.periodStart);
  if (!m) {
    for (let i = 0; i < dayCount; i++) {
      result.push({ date: String(i + 1), dow: 'Mon', isNonWorking: false });
    }
    return result;
  }
  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const nonWorking = new Set(props.nonWorkingDays);
  for (let i = 0; i < dayCount; i++) {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + i);
    result.push({
      date: formatMonthDay(d.toISOString().slice(0, 10)),
      dow: DOWS[d.getUTCDay()],
      isNonWorking: nonWorking.has(d.getUTCDay()),
    });
  }
  return result;
});

const rows = computed((): PreviewRow[] => buildPreviewRows({
  vpRows: props.vpRows,
  draftRows: props.draftRows,
  ghostCell: props.ghostCell,
}));

// Group header rows: "In Vantagepoint · Nh" above the VP block, "Draft · Nh" above the
// draft block (rows are already ordered existing-then-draft). Keyed by the
// index of the group's first row. Ghost rows don't count toward the total.
const groupHeaders = computed((): Map<number, { id: string; label: string }> => {
  const m = new Map<number, { id: string; label: string }>();
  const existing = rows.value.filter(r => !r.isDraft && !r.isGhost);
  const draft = rows.value.filter(r => r.isDraft || r.isGhost);
  if (existing.length > 0) {
    const hours = existing.reduce((s, r) => s + rowTotal(r), 0);
    m.set(0, { id: 'invp', label: `In Vantagepoint · ${hours.toFixed(2)}h` });
  }
  if (draft.length > 0) {
    const hours = draft.filter(r => !r.isGhost).reduce((s, r) => s + rowTotal(r), 0);
    m.set(existing.length, { id: 'draft', label: `Your Draft · ${hours.toFixed(2)}h` });
  }
  return m;
});

const dailyTotals = computed(() => {
  return periodDays.value.map((_, di) =>
    rows.value.reduce((sum, r) => sum + (r.days[di] ?? 0), 0),
  );
});

const grandTotal = computed(() =>
  dailyTotals.value.reduce((sum, t) => sum + t, 0),
);

function isHighlighted(row: PreviewRow): boolean {
  return isRowHighlighted(row, props.highlightWbs, props.highlightScope, props.highlightCell);
}

function isCellHighlighted(row: PreviewRow, dayIndex: number): boolean {
  return isCellHighlightedFor(row, dayIndex, props.highlightCell);
}

function phaseCellAriaLabel(row: PreviewRow): string | undefined {
  if (!row.isDraft || !row.isPhased) return undefined;
  if (row.needsPhase) return `${row.wbs1} — select phase`;
  return `${row.wbs1} phase ${row.wbs2} — tap to change`;
}

function taskCellAriaLabel(row: PreviewRow): string | undefined {
  if (!row.isDraft || !row.isPhased) return undefined;
  if (row.needsTask) return `${row.wbs1} ${row.wbs2} — select task`;
  if (row.wbs3) return `${row.wbs1} task ${row.wbs3} — tap to change`;
  return undefined;
}

</script>
